import { useEffect, useState, type FunctionComponent } from 'react';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import type { ActionCreator } from '@ardoq/rxbeach';
import { Box, FlexBox } from '@ardoq/layout';
import { Header4 } from '@ardoq/typography';
import { CenteredMessage } from 'tabview/CenteredMessage';
import { t } from '@ardoq/i18n';
import { GraphView, type Node } from '@ardoq/graph-view';
import { ViewLegendContainer } from '@ardoq/graph';
import { ViewLegend } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { HeightOffset } from 'views/viewLegend/useViewLegendSubscription';
import {
  canvasResolvedImageReady,
  svgImageReady,
} from 'tabview/canvasRendering/canvasResolvedImages';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import type { GroupFoldState } from '@ardoq/graph-view';
import { inspectComponent } from './interactions';
import {
  assistantDiagramFoldStateChanged,
  assistantDiagramZoomIn,
  assistantDiagramZoomOut,
  collapseAllAssistantDiagramGroups,
  expandAllAssistantDiagramGroups,
  optimizeAssistantDiagramLayout,
} from './assistantDiagram$';
import { AssistantDiagramViewModel, viewModel$ } from './viewModel$';

/**
 * The component cards draw icon/image sprites that may load asynchronously. When a
 * sprite is ready it dispatches a canvas-ready action; bumping this token then
 * triggers a graph repaint (via GraphView's `invalidateToken`) without re-running
 * layout. Mirrors the Modernized Block Diagram's redraw-on-image-ready handling.
 */
const useCanvasRedrawToken = (): number => {
  const [token, setToken] = useState(0);
  useEffect(() => {
    const bump = () => setToken(current => current + 1);
    const subscriptions = [
      subscribeToAction(svgImageReady, bump),
      subscribeToAction(canvasResolvedImageReady, bump),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, []);
  return token;
};

/**
 * A monotonic token bumped each time `action` fires (dispatched from the view-pane
 * registration in `viewModel$`). Passing the token to GraphView turns a fire-and-forget
 * view-pane action into the one-shot imperative trigger GraphView expects — for "optimize
 * layout", zoom, and collapse/expand-all — keeping the trigger decoupled from registration.
 */
const useActionToken = (action: ActionCreator<void>): number => {
  const [token, setToken] = useState(0);
  useEffect(() => {
    const subscriptions = [
      subscribeToAction(action, () => setToken(current => current + 1)),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, [action]);
  return token;
};

/**
 * Beta viewpoint-mode view that renders an AI-generated diagram. The Ardoq AI
 * Assistant emits a small declarative recipe (layout + grouping/coloring rules)
 * via its `generateDiagram` frontend tool; the recipe is applied to the graph this view
 * already holds (`applyRecipe`) and laid out + rendered by `@ardoq/graph-view`
 * (yFiles). There is no in-view input — the user drives it through the Assistant.
 */

const onNodeClick = (node: Node): void => inspectComponent(node.id);

const reportFoldState = (foldState: GroupFoldState): void =>
  dispatchAction(assistantDiagramFoldStateChanged(foldState));

export const AssistantDiagram = (props: AssistantDiagramViewModel) => {
  const { graphProps, i18nLocale, title, error, isLegendActive, legendProps } =
    props;

  const redrawToken = useCanvasRedrawToken();
  const relayoutToken = useActionToken(optimizeAssistantDiagramLayout);
  const zoomInToken = useActionToken(assistantDiagramZoomIn);
  const zoomOutToken = useActionToken(assistantDiagramZoomOut);
  const collapseAllToken = useActionToken(collapseAllAssistantDiagramGroups);
  const expandAllToken = useActionToken(expandAllAssistantDiagramGroups);
  if (!graphProps) {
    if (error) {
      return (
        <CenteredMessage
          title={t(
            i18nLocale,
            'assistantDiagram:errorState.title',
            'Could not generate the diagram'
          )}
          description={error}
        />
      );
    }
    return (
      <CenteredMessage
        title={t(
          i18nLocale,
          'assistantDiagram:emptyState.title',
          'No diagram yet'
        )}
        description={t(
          i18nLocale,
          'assistantDiagram:emptyState.description',
          'Ask the Ardoq AI Assistant to visualize the current view.'
        )}
      />
    );
  }
  return (
    <Box
      className="prevent-context-change"
      width="full"
      height="full"
      position="relative"
    >
      <FlexBox flexDirection="column" width="full" height="full">
        {title && (
          <Box padding="small">
            <Header4>{title}</Header4>
          </Box>
        )}
        <FlexBox flex={1} position="relative">
          <GraphView
            {...graphProps}
            onNodeClick={onNodeClick}
            invalidateToken={redrawToken}
            collapsibleGroups
            relayoutToken={relayoutToken}
            zoomInToken={zoomInToken}
            zoomOutToken={zoomOutToken}
            collapseAllToken={collapseAllToken}
            expandAllToken={expandAllToken}
            onFoldStateChange={reportFoldState}
          />
        </FlexBox>
      </FlexBox>
      <HeightOffset>
        {heightOffset => (
          <ViewLegendContainer
            heightOffset={heightOffset}
            visible={isLegendActive}
          >
            <ViewLegend
              {...legendProps}
              labels={getViewLegendLabels(i18nLocale)}
            />
          </ViewLegendContainer>
        )}
      </HeightOffset>
    </Box>
  );
};

export const ViewpointModeAssistantDiagram: FunctionComponent = connect(
  AssistantDiagram,
  viewModel$
);
